import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { appAbsoluteUrl } from "@/lib/app-base";
import { apiBaseURL } from "@/lib/api-config";
import { useAuthStore } from "@/stores/authStore";

const baseURL = apiBaseURL;

export const api = axios.create({
  baseURL,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    return null;
  }

  try {
    const { data } = await axios.post<{
      access_token: string;
      refresh_token: string;
    }>(`${baseURL}/api/auth/refresh`, { refresh_token: refreshToken });

    // Guard: if user logged out while refresh in-flight, do not restore tokens.
    if (useAuthStore.getState().refreshToken !== refreshToken) {
      return null;
    }

    useAuthStore.getState().setTokens({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    });

    return data.access_token;
  } catch {
    refreshPromise = null;
    try {
      await useAuthStore.getState().logout();
    } catch {
      // ignore
    }
    if (typeof window !== "undefined") {
      window.location.href = appAbsoluteUrl("login");
    }

    return null;
  }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Drop default JSON Content-Type for FormData uploads so the browser can
  // set "multipart/form-data; boundary=…" itself. Without this, the JSON
  // header overrides multipart and the server sees no file (Invalid upload).
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete (config.headers as Record<string, unknown>)["Content-Type"];
    }
  }

  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status;

    if (
      status !== 401 ||
      original._retry ||
      original.url?.includes("/auth/refresh") ||
      original.url?.includes("/auth/logout")
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const newAccess = await refreshPromise;
    if (!newAccess) {
      return Promise.reject(error);
    }

    original.headers.Authorization = `Bearer ${newAccess}`;

    return api(original);
  },
);
